import React from 'react';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { bindActionCreators } from 'redux';
import { withTranslation } from 'react-i18next';
import styled, { withTheme } from 'styled-components';
import _ from 'lodash';

import AlertPrompt from 'common/components/organisms/AlertPrompt';
import { ChevronDown, ChevronUp, Search } from 'common/images/FaIcons';
import FlexContainer from 'common/components/atoms/FlexContainer';
import EditIcon from 'common/images/BearingModal/EditIcon';
import RBAC from 'common/rbac/RBAC';
import {
  mapComponentToResource,
  operations
} from 'common/rbac/constants';
import { KEYBOARD_CODES } from 'common/constants';
import Button from 'common/components/atoms/Button';
import InputField_T from 'common/components/atoms/InputField';
import Filter from 'common/components/molecules/Filter';
import InlineModal from 'common/components/molecules/InlineModal';
import FilterItem from 'common/components/atoms/FilterItem';
import {
  isMachineCloneFailureStatus,
  isMachineCloneFinalStatus,
  isMachineCloneSuccessStatus,
  MachineCloneStatusEnum,
  normalizeMachineCloneStatus
} from 'common/constants/machineCloneStatus';
import { cloneStatusTheme } from 'common/helpers/cloneStatusTheme';
import {
  readCloneTasks,
  removeCloneTask,
  subscribeToCloneTasks,
  writeCloneTasks
} from 'common/helpers/cloneTaskStore';
import { parseErrorMessage } from 'common/helpers/errors';
import {
  ProgressFill,
  ProgressTrack,
  StatusBadge
} from 'common/components/molecules/CloneProgress.styled';
import AddTrendModal from 'home/Machines/MachineDetails/MachineCharts/components/AddTrendModal';
import CompareTagsModal from 'home/Machines/CompareTags/components/CompareTagsModal';
import { history } from 'common/helpers';
import * as assetDetailsActions from '../../actions/assetDetails.actions';
import * as hierarchyUpdateActions from '../../actions/hierarchy.actions';
import * as machineInfoActions from '../../../Machines/MachineDetails/MachineInfo/actions/machineInfo.actions';
import * as envelopeActions from '../../actions/envelope.actions';

import LeftColumn from '../atoms/LeftColumn';
import HeaderContainer_T from '../atoms/HeaderContainer';
import FooterFlex from '../atoms/FooterFlex';
import NodeList from '../organisms/NodeList';
import AddAreaModal from '../organisms/AddAreaModal';
import AssetPasteModal from '../organisms/AssetPasteModal';
import AssetCopyModal from '../organisms/AssetCopyModal';
import CloneMachinesModal from '../organisms/CloneMachinesModal';
import * as hierarchyUtils from '../../utils/assetHierarchyUtils';
import SearchList from '../organisms/SearchList';
import ScrollContainer from '../../../HomePage/components/atoms/ScrollContainer';
import DeleteOverallAlarmsModal from '../organisms/OverallAlarm/DeleteOverallAlarmsModal';
import {
  CloneProgressBody,
  CloneProgressCount,
  CloneProgressHeader,
  CloneProgressSection,
  CloneProgressTitle,
  CloneTaskCard,
  CloneTaskDismiss,
  CloneTaskHeader,
  CloneTaskLabel,
  CloneTaskMeta,
  MachineName,
  MachineRow,
  MachineRows
} from './HierarchyView.styled';


const Content = styled(FlexContainer)`
  background: white;
  width: 100%;
  /* Positioning context for the clone-progress overlay, which is anchored to the
     bottom of this pane rather than sitting after the tree in flow. */
  position: relative;
`;

const ColumnLayout = styled(FlexContainer).attrs({})`
  ${props => props.editMode ? 'height: calc(100% - 55px);' : 'height: 100%;'}
`;

const SvgRowContainer = styled(FlexContainer)`
  padding: 0;
  margin-left: auto;
  width: auto;
`;

const InputField = styled(InputField_T)`
  margin: auto 0;
`;

const SvgContainer = styled(FlexContainer)`
  cursor: pointer;
  padding: 0px 1.5em;
  .fa-sm {
    font-size: 1.2em;
  }
  align-items: center;
`;

const HeaderContainer = styled(HeaderContainer_T)`
  padding: 5px 2px 10px 2px;
  ${InlineModal} {
    left: 10px;
    width: max-content;
  }
  ${Filter} {
    margin-right: 10px;
    width: max-content
  }
  border-right: 2px solid ${props => props.theme.colors.greyXL};
  z-index: 5;
  background: white;
  display: ${props => props.isVisible ? 'flex' : 'none'};
`;

const ListOption = styled.span`
  margin-left: 10px;
  color: ${props => props.selected ? props.theme.colors.black : props.theme.colors.greyXD};
  font-weight: ${props => props.selected && 600};
  white-space: pre;
`;

const CustomScrollContainer = styled(ScrollContainer)`
    // 110px = top bar (BreadCrumb header) + HierarchyView search filters bar;
    // +45px = the org/site breadcrumb PillRow rendered above this scroll region
    // (OrgSiteBreadcrumb). Without it the tree runs past the panel and the last
    // node gets clipped.
    height: calc(100vh - var(--title-bar-offset, 0px) - 155px);
`;

// Trailing room inside the scrolled column so the tail of a long tree can be scrolled clear
// of the clone-progress overlay, which floats over the bottom of the pane.
//
// Two approaches were measured and rejected first. Reducing this scroller's height shrinks
// Content, the overlay's positioning parent, which drags the bottom-anchored overlay up and
// leaves dead space below it. `padding-bottom` on the scroller is simply not counted in its
// scrollable overflow here (scrollHeight excluded it), so the tail stayed stranded.
//
// A real element always counts. It also degrades well: a short tree plus this spacer still
// fits, so nothing becomes scrollable until the tree is long enough that its tail would
// genuinely have been hidden.
const CloneOverlaySpacer = styled.div`
  flex: 0 0 auto;
  height: ${props => props.height || 0}px;
`;

const CLONE_POLL_INTERVAL_MS = 1500;
// 1.5s x 2400 is roughly an hour of tracking before the UI gives up on a task.
const CLONE_POLL_MAX_ATTEMPTS = 2400;

class HierarchyView extends React.Component {
  constructor(props) {
    super(props);
    this.sortHierarchyAscending = JSON.parse(localStorage.getItem('sortHierarchyAscending'));
    const nodes = this.sortHierarchy(_.cloneDeep(this.props.assetHierarchy.hierarchy), this.sortHierarchyAscending);
    this.clonePollTimeouts = new Set();
    this.activeClonePollers = new Set();
    this.isClonePollingMounted = false;
    // Attached with innerRef, not ref: on styled-components v3 a plain ref resolves to the
    // StyledComponent instance, and measuring or observing that throws.
    this.cloneOverlayRef = React.createRef();
    this.unsubscribeCloneTasks = null;
    this.cloneOverlayObserver = null;
    const persistedCloneTasks = readCloneTasks(this.getCurrentUserId());
    this.state = {
      treeNodes: nodes,
      saveDisabled: true,
      currentConfigIndex: -1,
      configList: hierarchyUtils.getNodeListFromHierarchy(nodes, 'config'),
      searchKey: '',
      searchActive: false,
      nodeFilterExpanded: false,
      nodeFilterSelected: null,
      node: null,
      editNode: null,
      deleteModal: false,
      deleteInactiveTagsModal: false,
      addAreaModal: false,
      editAreaModal: false,
      assetCopyModal: false,
      assetPasteModal: false,
      deleteOverallAlarmsModal: false,
      copiedAssetDetails: null,
      addTrendModal: false,
      compareTagsModal: false,
      cloneMachinesModal: false,
      cloneTargetAccountId: null,
      cloneTaskProgressList: persistedCloneTasks,
      // Expanded by default: a running clone is the reason the panel appears at all.
      cloneProgressExpanded: true,
      cloneOverlayHeight: 0,
      cloneTaskPendingDismissal: null
    };
    this.filterOptions = [
      { textKey: 'assetHierarchy.filters.area', node_type: 'area' },
      { textKey: 'assetHierarchy.filters.asset', node_type: 'machine' }
    ];
  }

  getCurrentUserId = () => {
    const { currentUser } = this.props;
    return (currentUser && currentUser.id) || null;
  }

  toggleCloneProgressExpanded = () => {
    this.setState(prevState => ({ cloneProgressExpanded: !prevState.cloneProgressExpanded }));
  }

  storeCloneTasks = (cloneTaskProgressList) => {
    // silent: this component is the writer, so re-entering its own subscriber would
    // loop against componentDidUpdate.
    writeCloneTasks(this.getCurrentUserId(), cloneTaskProgressList, { silent: true });
  }

  /**
   * Picks up tasks written by another surface (the Add Account modal) or another tab,
   * and starts tracking any that are still running.
   */
  handleExternalCloneTasks = (userId, tasks) => {
    if (!this.isClonePollingMounted) return;
    if (String(userId) !== String(this.getCurrentUserId())) return;
    if (_.isEqual(tasks, this.state.cloneTaskProgressList)) return;

    this.setState({ cloneTaskProgressList: tasks }, () => {
      tasks
        .filter(task => !isMachineCloneFinalStatus(task.status))
        .forEach(task => this.startCloneTaskPolling(task.taskId));
    });
  }

  requestCloneTaskDismissal = (task) => {
    // A finished task is just history, so it goes without ceremony. A running one is
    // the only way to watch that clone, so it needs the warning.
    if (isMachineCloneFinalStatus(task.status)) {
      this.dismissCloneTask(task.taskId);
      return;
    }
    this.setState({ cloneTaskPendingDismissal: task });
  }

  cancelCloneTaskDismissal = () => {
    this.setState({ cloneTaskPendingDismissal: null });
  }

  dismissCloneTask = (taskId) => {
    const remaining = removeCloneTask(this.getCurrentUserId(), taskId);
    this.activeClonePollers.delete(String(taskId));
    this.setState({
      cloneTaskProgressList: remaining,
      cloneTaskPendingDismissal: null
    });
  }

  /**
   * The overlay's height feeds the tree scroller so the tree never ends up underneath
   * it. It changes as tasks stream in and when the panel is collapsed, hence the
   * observer rather than a constant.
   */
  // The ref must resolve to a real DOM element: measuring and observing both throw on
  // anything else, and styled-components v3 hands back a component instance for `ref`.
  getCloneOverlayElement = () => {
    const node = this.cloneOverlayRef.current;
    return (node && node.nodeType === 1) ? node : null;
  }

  measureCloneOverlay = () => {
    const node = this.getCloneOverlayElement();
    const cloneOverlayHeight = node ? Math.round(node.getBoundingClientRect().height) : 0;
    if (cloneOverlayHeight !== this.state.cloneOverlayHeight) {
      this.setState({ cloneOverlayHeight });
    }
  }

  observeCloneOverlay = () => {
    const node = this.getCloneOverlayElement();
    if (this.cloneOverlayObserver) {
      this.cloneOverlayObserver.disconnect();
      this.cloneOverlayObserver = null;
    }
    if (!node || typeof ResizeObserver === 'undefined') {
      // No element or no observer support: fall back to a one-off measurement, so the
      // tree still gets its bottom offset even if it stops tracking size changes.
      this.measureCloneOverlay();
      return;
    }
    this.cloneOverlayObserver = new ResizeObserver(() => this.measureCloneOverlay());
    this.cloneOverlayObserver.observe(node);
    this.measureCloneOverlay();
  }

  componentDidMount() {
    this.isClonePollingMounted = true;
    this.setCurrentConfigIndex();
    this.resumeStoredCloneTasks();
    this.unsubscribeCloneTasks = subscribeToCloneTasks(this.handleExternalCloneTasks);
    this.observeCloneOverlay();
  }

  componentWillUnmount() {
    this.isClonePollingMounted = false;
    this.clonePollTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.clonePollTimeouts.clear();
    this.activeClonePollers.clear();
    if (this.unsubscribeCloneTasks) this.unsubscribeCloneTasks();
    if (this.cloneOverlayObserver) this.cloneOverlayObserver.disconnect();
    this.removeWindowEvenListener();
  }

  setCurrentConfigIndex = () => {
    const { machine_id, config_id } = hierarchyUtils.getSelectedPathFromBreadcrumb(this.props.breadcrumb);
    const { configList, treeNodes } = this.state;
    if (config_id && config_id >= 0) {
      const currentConfigIndex = _.findIndex(configList, config => config.id === config_id);
      this.setState({ currentConfigIndex });
    } else if (machine_id) {
      const config = hierarchyUtils.getNextConfigByMachineId(treeNodes, machine_id);
      if (config) {
        const configIndx = _.findIndex(configList, cf => cf.id === config.id);
        const currentConfigIndex = configIndx - 1;
        this.setState({ currentConfigIndex });
      }
    } else {
      this.setState({ currentConfigIndex: -1 });
    }
  }

  onClickUpArrow = () => {
    let { currentConfigIndex } = this.state;
    const { configList } = this.state;
    if (currentConfigIndex - 1 >= 0) {
      currentConfigIndex--;
      this.setState({ currentConfigIndex });
      const selectedConfig = configList[currentConfigIndex];
      this.selectNode(selectedConfig.id, selectedConfig.node_type);
    }
  }

  onClickDownArrow = () => {
    let { currentConfigIndex } = this.state;
    const { configList } = this.state;
    if (currentConfigIndex + 1 < configList.length) {
      currentConfigIndex++;
      this.setState({ currentConfigIndex });
      const selectedConfig = configList[currentConfigIndex];
      this.selectNode(selectedConfig.id, selectedConfig.node_type);
    }
  }

  handleKeyStrokes = (e) => {
    const { currentConfigIndex, configList } = this.state;
    if (e.keyCode !== KEYBOARD_CODES.UP_ARROW && e.keyCode !== KEYBOARD_CODES.DOWN_ARROW) return;
    if (e.keyCode === KEYBOARD_CODES.DOWN_ARROW && currentConfigIndex + 1 < configList.length) {
      this.onClickDownArrow();
    } else if (e.keyCode === KEYBOARD_CODES.UP_ARROW && currentConfigIndex - 1 >= 0) {
      this.onClickUpArrow();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.assetHierarchy.hierarchy !== prevProps.assetHierarchy.hierarchy) {
      this.setTreeNodesAndConfigList();
    }
    if (
      prevProps.breadcrumb.machine.id !== this.props.breadcrumb.machine.id ||
      prevProps.breadcrumb.config.id !== this.props.breadcrumb.config.id
    ) {
      this.setCurrentConfigIndex();
    }
    if (prevProps.assetHierarchy.hierarchy !== this.props.assetHierarchy.hierarchy) {
      this.resumeStoredCloneTasks();
    }
    if (prevState.cloneTaskProgressList !== this.state.cloneTaskProgressList) {
      this.storeCloneTasks(this.state.cloneTaskProgressList);
    }
    // The overlay mounts and unmounts with the task list, so the observer has to follow it.
    const overlayWasVisible = !!prevState.cloneTaskProgressList.length;
    const overlayIsVisible = !!this.state.cloneTaskProgressList.length;
    if (overlayWasVisible !== overlayIsVisible) {
      this.observeCloneOverlay();
    }
  }

  setSavedisableState = (saveDisabled) => {
    this.setState({ saveDisabled });
  }

  reorderTree = (oldIndex, newIndex, node_type, node_id, nodes = this.state.treeNodes) => {
    nodes.forEach((node) => {
      if (node.node_type === node_type && node.id === node_id) {
        const el = nodes.splice(oldIndex, 1);
        nodes.splice(newIndex, 0, el[0]);
        this.setState({
          treeNodes: this.state.treeNodes,
          saveDisabled: false
        });
        // eslint-disable-next-line no-throw-literal
        throw null;
      }
      this.reorderTree(oldIndex, newIndex, node_type, node_id, node.children);
    });
  };

  setOrToggleNodeExpand = (nodeId, nodeType, nodes = this.state.treeNodes, expand = false) => {
    nodes.forEach((node) => {
      if (node.id === nodeId && node.node_type === nodeType) {
        node.expanded = !node.expanded;
        if (expand) node.expanded = true;
        this.setState({
          treeNodes: this.state.treeNodes
        });

        this.props.hierarchyUpdateActions.toggleNodeExpand(nodeId, nodeType);
        return;
      }
      this.setOrToggleNodeExpand(nodeId, nodeType, node.children, expand);
    });
  };

  selectNode = (nodeId, nodeType, expand = false, onlyToggleExpand = false) => {
    if (nodeType !== 'site') this.setOrToggleNodeExpand(nodeId, nodeType, this.state.treeNodes, expand);
    if (this.props.editMode) return;
    const { selected } = this.props.breadcrumb;
    if (nodeType === 'machine' && selected.id === nodeId && selected.type === nodeType && !expand) return;
    if (nodeType === 'location' && selected.id === nodeId && selected.type === nodeType) return;
    if (onlyToggleExpand) return;
    this.props.handleNodeClick(nodeId, nodeType, 'overview', false, null, true);
  };

  onEdit = (editMode) => {
    if (editMode) {
      this.props.setEditModeState(editMode);
    } else this.onCancel();
  }

  onCancel = () => {
    this.setTreeNodesAndConfigList();
    this.props.setEditModeState(false);
    this.setSavedisableState(true);
  }

  setSearchKey = key => this.setState({ searchKey: key });


  onSave = () => {
    const [areasPerSite, machinesPerArea] = hierarchyUtils.getUpdatedAreasAndMachines(this.props.assetHierarchy.hierarchy, this.state.treeNodes);
    const { assetDetailsActions } = this.props;
    machinesPerArea.forEach((area, idx) => assetDetailsActions.updateMachines(area, idx === machinesPerArea.length - 1));
    areasPerSite.forEach((site, idx) => assetDetailsActions.updateAreas(site, idx === areasPerSite.length - 1));
    this.setSavedisableState(true);
    this.props.setEditModeState(false);
  }

  toggleFilter = () => {
    this.setState(prevState => ({
      nodeFilterExpanded: !prevState.nodeFilterExpanded
    }));
  }

  selectFilter = (filter) => {
    this.setState({ nodeFilterSelected: filter });
  }

  handleFilterItemClick = (option) => {
    const { nodeFilterSelected } = this.state;
    this.toggleFilter();
    if (option.node_type === nodeFilterSelected) {
      this.setState({ nodeFilterSelected: null });
    } else {
      this.selectFilter(option.node_type);
      this.focusSearch();
    }
  }

  addWindowEventListener = () => {
    window.addEventListener('keydown', this.handleKeyStrokes);
  }

  removeWindowEvenListener = () => {
    window.removeEventListener('keydown', this.handleKeyStrokes);
  }

  focusSearch = () => {
    const input = document.querySelector('input[name="hierarchy-search"]');
    input.focus();
  }

  scrollNodeInView = (nodeType, nodeId) => {
    // TODO: use something beter than setTimeout
    setTimeout(() => {
      const node = document.querySelector(`span[name="${nodeType}_${nodeId}"]`);
      if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }

  deleteArea = (node) => {
    this.props.hierarchyUpdateActions.deleteArea(node.id).then(
      (res) => {
        toastr.success(res.message);
        this.props.assetDetailsActions.getHierarchy(false);
      },
      (error) => {
        toastr.error(error.message);
      }
    );
  }

  deleteMachine = (node) => {
    this.props.machineInfoActions.deleteMachine(node.id).then(
      (res) => {
        toastr.success(res.message);
        this.props.assetDetailsActions.getHierarchy(false);
      },
      (error) => {
        toastr.error(error.message);
      }
    );
  }


  deleteInactiveTags = (node) => {
    this.props.hierarchyUpdateActions.deleteTags(node).then(
      (res) => {
        toastr.success(res.message);
      },
      (error) => {
        toastr.error(error.message);
      }
    );
    this.toggleDeleteInactiveTagsModal();
  }

  openCloneMachinesModal = (targetAccountId) => {
    this.setState({
      cloneMachinesModal: true,
      cloneTargetAccountId: targetAccountId
    });
  }

  closeCloneMachinesModal = () => {
    this.setState({
      cloneMachinesModal: false,
      cloneTargetAccountId: null
    });
  }

  fetchSourceSites = () => this.props.hierarchyUpdateActions.fetchSourceSites();

  fetchSourceMachines = sourceAccountId => this.props.hierarchyUpdateActions.fetchSourceMachines(sourceAccountId);

  submitCloneToAccount = payload => this.props.hierarchyUpdateActions.submitCloneToAccount(payload);

  pollCloneTask = (taskId, onProgress, attempt = 0) => this.props.hierarchyUpdateActions.getCloneTaskStatus(taskId).then(
    (taskState) => {
      // The in-flight request is not cancellable, so it can resolve after the view
      // unmounted. Without this check the chain re-arms itself and every remount
      // leaves another poll loop running for the life of the tab.
      if (!this.isClonePollingMounted) {
        return taskState;
      }
      if (onProgress) {
        onProgress(taskState);
      }
      if (!taskState) {
        return taskState;
      }

      const taskStatus = this.getTaskLifecycleStatus(taskState, MachineCloneStatusEnum.IN_PROGRESS);
      const completedMachines = Number(taskState.completed_machines);
      const totalMachines = Number(taskState.total_machines);

      if (isMachineCloneFailureStatus(taskStatus)) {
        throw new Error(taskState.status || this.props.t('assetHierarchy.cloneMachines.failed', { defaultValue: 'Machine clone failed.' }));
      }

      if (isMachineCloneSuccessStatus(taskStatus) || (Number.isFinite(completedMachines) && Number.isFinite(totalMachines) && totalMachines > 0 && completedMachines >= totalMachines)) {
        return taskState;
      }

      // A revoked task, or one whose celery result has expired, never reaches a
      // final status, so cap the chain instead of polling a dead id forever.
      if (attempt + 1 >= CLONE_POLL_MAX_ATTEMPTS) {
        throw new Error(this.props.t('assetHierarchy.cloneMachines.timedOut', {
          defaultValue: 'Stopped tracking this clone after it stayed in progress too long.'
        }));
      }

      return new Promise((resolve) => {
        const timeoutId = setTimeout(() => {
          this.clonePollTimeouts.delete(timeoutId);
          if (!this.isClonePollingMounted) {
            resolve(taskState);
            return;
          }
          resolve(this.pollCloneTask(taskId, onProgress, attempt + 1));
        }, CLONE_POLL_INTERVAL_MS);
        this.clonePollTimeouts.add(timeoutId);
      });
    }
  );

  getSiteNameById = (siteId, fallbackSites) => {
    const siteNode = (this.state.treeNodes || []).find(node => node.node_type === 'site' && Number(node.id) === Number(siteId));
    if (siteNode && siteNode.node_name) {
      return siteNode.node_name;
    }
    // The tree only holds the sites in view, so fall back to the site list the
    // clone modal loaded before resorting to the bare "Site 12" placeholder.
    const knownSite = (fallbackSites || []).find(site => Number(site.id) === Number(siteId));
    if (knownSite && knownSite.name) {
      return knownSite.name;
    }
    return `${this.props.t('common.site', { defaultValue: 'Site' })} ${siteId}`;
  }

  getTaskMachineProgress = taskState => (
    (taskState && taskState.machine_progress)
    || (taskState && taskState.result && taskState.result.machine_progress)
    || (taskState && taskState.meta && taskState.meta.machine_progress)
    || {}
  )

  isKnownMachineCloneStatus = (status) => {
    const normalizedStatus = normalizeMachineCloneStatus(status);
    return [
      MachineCloneStatusEnum.PENDING,
      MachineCloneStatusEnum.IN_PROGRESS,
      MachineCloneStatusEnum.COMPLETED,
      MachineCloneStatusEnum.FAILED,
      MachineCloneStatusEnum.SKIPPED
    ].includes(normalizedStatus);
  }

  getTaskLifecycleStatus = (taskState, fallbackStatus = MachineCloneStatusEnum.IN_PROGRESS) => {
    const stateCandidates = [
      taskState && taskState.state,
      taskState && taskState.result && taskState.result.state,
      taskState && taskState.meta && taskState.meta.state
    ];
    const statusCandidates = [
      taskState && taskState.status,
      taskState && taskState.result && taskState.result.status,
      taskState && taskState.meta && taskState.meta.status
    ];

    for (let i = 0; i < stateCandidates.length; i += 1) {
      if (this.isKnownMachineCloneStatus(stateCandidates[i])) {
        return normalizeMachineCloneStatus(stateCandidates[i]);
      }
    }

    for (let i = 0; i < statusCandidates.length; i += 1) {
      if (this.isKnownMachineCloneStatus(statusCandidates[i])) {
        return normalizeMachineCloneStatus(statusCandidates[i]);
      }
    }

    return normalizeMachineCloneStatus(fallbackStatus);
  }

  getFailedMachineIds = (taskState) => {
    const failedIds = (taskState && taskState.failed_machine_ids)
      || (taskState && taskState.result && taskState.result.failed_machine_ids)
      || (taskState && taskState.meta && taskState.meta.failed_machine_ids)
      || [];
    return new Set((failedIds || []).map(id => String(id)));
  }

  buildMachineRows = (existingRows = [], machineStatusMap = {}) => existingRows.map((machine) => {
    const progress = machineStatusMap[machine.id] || machineStatusMap[String(machine.id)] || {};
    return {
      ...machine,
      status: normalizeMachineCloneStatus(progress.status || machine.status),
      new_machine_name: progress.new_machine_name || machine.new_machine_name
    };
  })

  getCloneSummary = (taskState, fallbackTotal = 0) => ({
    completed_machines: Number(taskState && taskState.completed_machines) || 0,
    total_machines: Number(taskState && taskState.total_machines) || fallbackTotal
  })

  updateCloneTask = (taskId, updater) => {
    if (!this.isClonePollingMounted) return;
    this.setState(prevState => ({
      cloneTaskProgressList: prevState.cloneTaskProgressList.map(task => (
        String(task.taskId) === String(taskId) ? updater(task) : task
      ))
    }));
  }

  handleCloneTaskStarted = ({ taskId, targetAccountId, selectedMachines, sourceSites }) => {
    const machineRows = (selectedMachines || []).map(machine => ({
      id: machine.id,
      name: machine.name,
      sourceAccountName: machine.sourceAccountName || '',
      status: MachineCloneStatusEnum.PENDING,
      new_machine_name: ''
    }));

    // Which site each machine came from, so the panel can say where the assets
    // went from and to rather than only naming the target.
    const sourceAccountNames = Array.from(new Set(
      machineRows.map(machine => machine.sourceAccountName).filter(name => !!name)
    ));

    const cloneTask = {
      taskId: String(taskId),
      targetAccountId: Number(targetAccountId),
      targetAccountName: this.getSiteNameById(targetAccountId, sourceSites),
      sourceAccountNames,
      status: MachineCloneStatusEnum.IN_PROGRESS,
      errorMessage: '',
      startedAt: new Date().toISOString(),
      summary: {
        completed_machines: 0,
        total_machines: machineRows.length
      },
      machineRows
    };

    if (!this.isClonePollingMounted) return;
    this.setState(prevState => ({
      cloneTaskProgressList: [
        cloneTask,
        ...prevState.cloneTaskProgressList.filter(task => String(task.taskId) !== String(taskId))
      ]
    }), () => this.startCloneTaskPolling(cloneTask.taskId));
  }

  handleCloneTaskProgress = (taskId, taskState) => {
    this.updateCloneTask(taskId, (task) => {
      const machineStatusMap = this.getTaskMachineProgress(taskState);
      return {
        ...task,
        status: this.getTaskLifecycleStatus(taskState, task.status || MachineCloneStatusEnum.IN_PROGRESS),
        summary: this.getCloneSummary(taskState, task.machineRows.length),
        machineRows: this.buildMachineRows(task.machineRows, machineStatusMap)
      };
    });
  }

  handleCloneTaskFinished = (taskId, taskState) => {
    this.updateCloneTask(taskId, (task) => {
      const machineStatusMap = this.getTaskMachineProgress(taskState);
      const failedMachineIds = this.getFailedMachineIds(taskState);
      const finalizedRows = this.buildMachineRows(task.machineRows, machineStatusMap).map((machine) => {
        if (failedMachineIds.has(String(machine.id))) {
          return {
            ...machine,
            status: MachineCloneStatusEnum.FAILED
          };
        }
        return machine;
      });
      const hasFailures = failedMachineIds.size > 0
        || finalizedRows.some(machine => isMachineCloneFailureStatus(machine.status));
      const taskStatus = hasFailures ? MachineCloneStatusEnum.FAILED : MachineCloneStatusEnum.COMPLETED;
      return {
        ...task,
        status: taskStatus,
        errorMessage: hasFailures
          ? this.props.t('assetHierarchy.cloneMachines.partialFailure', { defaultValue: 'Machine clone completed with some failures.' })
          : '',
        summary: this.getCloneSummary(taskState, finalizedRows.length),
        machineRows: finalizedRows
      };
    });
  }

  handleCloneTaskFailed = (taskId, error) => {
    const errorMessage = parseErrorMessage(error)
      || this.props.t('assetHierarchy.cloneMachines.failed', { defaultValue: 'Machine clone failed.' });

    // The inline panel text is easy to miss and invisible when the pane is
    // collapsed or scrolled, so raise a toast as well. The panel keeps the
    // message afterwards, because the toast disappears.
    const failedTask = (this.state.cloneTaskProgressList || [])
      .find(task => String(task.taskId) === String(taskId));
    const targetName = (failedTask && failedTask.targetAccountName) || '';
    toastr.error(targetName
      ? this.props.t('assetHierarchy.cloneMachines.failedForSite', {
        defaultValue: 'Clone to {{siteName}} failed: {{reason}}',
        siteName: targetName,
        reason: errorMessage
      })
      : errorMessage);

    this.updateCloneTask(taskId, task => ({
      ...task,
      status: MachineCloneStatusEnum.FAILED,
      errorMessage,
      machineRows: task.machineRows.map((machine) => {
        const status = normalizeMachineCloneStatus(machine.status);
        if (isMachineCloneFinalStatus(status)) return machine;
        return {
          ...machine,
          status: MachineCloneStatusEnum.FAILED
        };
      })
    }));
  }

  startCloneTaskPolling = (taskId) => {
    const normalizedTaskId = String(taskId);
    if (this.activeClonePollers.has(normalizedTaskId)) return;

    this.activeClonePollers.add(normalizedTaskId);
    this.pollCloneTask(normalizedTaskId, taskState => this.handleCloneTaskProgress(normalizedTaskId, taskState))
      .then((taskState) => {
        if (!this.isClonePollingMounted) return;
        this.handleCloneTaskFinished(normalizedTaskId, taskState);
        this.handleCloneCompleted();
      })
      .catch((error) => {
        if (!this.isClonePollingMounted) return;
        this.handleCloneTaskFailed(normalizedTaskId, error);
      })
      .finally(() => {
        this.activeClonePollers.delete(normalizedTaskId);
      });
  }

  resumeStoredCloneTasks = () => {
    this.state.cloneTaskProgressList
      .filter(task => !isMachineCloneFinalStatus(task.status))
      .forEach(task => this.startCloneTaskPolling(task.taskId));
  }

  handleCloneCompleted = () => {
    this.props.assetDetailsActions.getHierarchy(false);
  }

  getNodeMenuOptions = (node) => {
    const { t } = this.props;
    const nodeType = node.node_type;
    switch (nodeType) {
      case 'site': {
        const options = [
          {
            text: t('assetHierarchy.nodeMenu.addNode'),
            onClick: () => this.openAddNodeModal(node),
            resource: mapComponentToResource.Areas,
            operation: operations.Create
          },
          {
            text: t('assetHierarchy.nodeMenu.edit'),
            onClick: () => history.push('/site-profile'),
            resource: mapComponentToResource.Accounts,
            operation: operations.Update
          },
          {
            text: t('assetHierarchy.nodeMenu.cloneMachines', { defaultValue: 'Clone Machines' }),
            onClick: () => this.openCloneMachinesModal(node.id),
            resource: mapComponentToResource.MachineClone,
            operation: operations.Create
          },
          {
            text: t('assetHierarchy.nodeMenu.sortAZ'),
            onClick: () => this.setSortOrder(true),
            iconOperation: 'sort-ascending',
            selected: this.sortHierarchyAscending === true
          },
          {
            text: t('assetHierarchy.nodeMenu.sortZA'),
            onClick: () => this.setSortOrder(false),
            iconOperation: 'sort-descending',
            selected: this.sortHierarchyAscending === false
          },
          {
            text: t('assetHierarchy.nodeMenu.deleteAllInactiveTags'),
            onClick: () => this.toggleDeleteInactiveTagsModal({ account_id: node.id }),
            resource: mapComponentToResource.Tags,
            operation: operations.Delete
          },
          {
            text: t('assetHierarchy.nodeMenu.restoreListOrder'),
            onClick: () => this.restoreOrginalOrder(),
            iconOperation: 'restore-list',
            selected: this.sortHierarchyAscending === null
          }
        ];
        return options;
      }
      case 'area': {
        const options = [
          {
            text: t('assetHierarchy.nodeMenu.addNode'),
            onClick: () => this.openAddNodeModal(node),
            resource: mapComponentToResource.Areas,
            operation: operations.Create
          },
          {
            text: t('assetHierarchy.nodeMenu.edit'),
            onClick: () => this.openEditAreaModal(node, true),
            resource: mapComponentToResource.Areas,
            operation: operations.Update
          },
          {
            text: t('assetHierarchy.nodeMenu.compare'),
            onClick: () => this.openCompareTagsModal(),
            resource: mapComponentToResource.ChartMultilineItem,
            iconOperation: 'compare',
            operation: operations.Create
          },
          {
            text: t('assetHierarchy.nodeMenu.deleteAllInactiveTags'),
            onClick: () => this.toggleDeleteInactiveTagsModal({ area_id: node.id, account_id: this.props.currentUser.account_id }),
            resource: mapComponentToResource.Tags,
            operation: operations.Delete
          },
          {
            text: t('assetHierarchy.nodeMenu.delete'),
            onClick: () => this.openDeleteModal(node),
            resource: mapComponentToResource.Areas,
            operation: operations.Delete
          }
        ];
        if (node.children.length) options.pop();
        if (this.state.copiedAssetDetails) {
          options.push({
            text: t('assetHierarchy.nodeMenu.pasteAsset'),
            onClick: () => this.openAssetPasteModal(node),
            resource: mapComponentToResource.Machines,
            iconOperation: 'paste',
            operation: operations.Create
          });
        }
        return options;
      }
      case 'machine':
        return [
          {
            text: t('assetHierarchy.nodeMenu.edit'),
            onClick: () => {
              this.selectNode(node.id, node.node_type, true);
              history.push(`/machines/${node.id}/info`);
            },
            resource: mapComponentToResource.Machines,
            operation: operations.Update
          },
          {
            text: t('assetHierarchy.nodeMenu.addChart'),
            onClick: () => this.openAddTrendModal(node),
            resource: mapComponentToResource.ChartMultilineItem,
            operation: operations.Create
          },
          {
            text: t('assetHierarchy.nodeMenu.copyAsset'),
            onClick: () => this.openAssetCopyModal(node),
            resource: mapComponentToResource.Machines,
            iconOperation: 'copy',
            operation: operations.Create
          },
          {
            text: t('assetHierarchy.nodeMenu.delete'),
            onClick: () => this.openDeleteModal(node),
            resource: mapComponentToResource.Machines,
            operation: operations.Delete
          },
          {
            text: t('assetHierarchy.nodeMenu.deleteEnvelopeAlarms'),
            onClick: () => this.openDeleteModal({ node_type: 'tags_envelopes', id: node.id }),
            resource: mapComponentToResource.Tags,
            operation: operations.Delete
          },
          {
            text: t('assetHierarchy.nodeMenu.deleteOverallAlarms'),
            onClick: () => this.openDeleteOverallAlarmsModal(node),
            resource: mapComponentToResource.Tags,
            operation: operations.Delete
          },
          {
            text: t('assetHierarchy.nodeMenu.deleteAllInactiveTags'),
            onClick: () => this.toggleDeleteInactiveTagsModal({ machine_id: node.id, account_id: this.props.currentUser.account_id }),
            resource: mapComponentToResource.Tags,
            operation: operations.Delete
          },
          {
            text: t('assetHierarchy.nodeMenu.compare'),
            onClick: () => this.openCompareTagsModal(node),
            resource: mapComponentToResource.ChartMultilineItem,
            iconOperation: 'compare',
            operation: operations.Create
          }
        ];
      default:
        return [];
    }
  }

  openDeleteModal = (node) => {
    this.setState({
      deleteModal: true,
      editNode: { ...node }
    });
  }

  closeDeleteModal = () => {
    this.setState({
      deleteModal: false,
      editNode: null
    });
  }

  toggleDeleteInactiveTagsModal = (node = {}) => {
    this.setState(
      prevState => ({
        deleteInactiveTagsModal: !prevState.deleteInactiveTagsModal,
        editNode: { ...node }
      }));
  }

  onDeleteEnvelopeAlarms = (node) => {
    const { breadcrumb: { site } } = this.props;
    this.props.assetDetailsActions.getTags(node.id, site.id, false).then(
      (res) => {
        let tags = _.filter(res, (tag => tag.type === 'vibration'));
        tags = _.map(tags, t => ({ id: t.id, spectral_envelope: null, envelope_settings: null }));
        this.props.envelopeActions.bulkUpdateTags(node.id, { tags }).then(
          (res) => {
            toastr.success(res.message);
            this.props.assetDetailsActions.getTags(node.id, site.id);
          },
          (error) => {
            toastr.error(error.message);
          }
        );
      }
    );
  }

  deleteNode = (node) => {
    const nodeType = node.node_type;
    switch (nodeType) {
      case 'area':
        this.deleteArea(node);
        break;
      case 'machine':
        this.deleteMachine(node);
        break;
      case 'tags_envelopes':
        this.onDeleteEnvelopeAlarms(node);
        break;
      default:
        break;
    }
    this.closeDeleteModal();
  }

  openEditAreaModal = (node) => {
    this.setState({
      editAreaModal: true,
      editNode: { ...node }
    });
  }

  openAddNodeModal = (node) => {
    this.setState({
      addAreaModal: true,
      node: { ...node }
    });
  }

  closeAddNodeModal = () => {
    this.setState({
      addAreaModal: false,
      node: null
    });
  }

  closeAreaEditModal = () => {
    this.setState({
      editAreaModal: false,
      editNode: null
    });
  }

  openAssetCopyModal = (node) => {
    this.setState({
      assetCopyModal: true,
      editNode: { ...node }
    });
  }

  closeAssetCopyModal = () => {
    this.setState({
      assetCopyModal: false,
      editNode: null
    });
  }

  openAssetPasteModal = (node) => {
    this.setState({
      assetPasteModal: true,
      editNode: { ...node }
    });
  }

  closeAssetPasteModal = () => {
    this.setState({
      assetPasteModal: false,
      editNode: null
    });
  }

  openAddTrendModal = (node) => {
    this.setState({
      addTrendModal: true,
      editNode: { ...node }
    });
  }

  closeAddTrendModal = () => {
    this.setState({
      addTrendModal: false,
      editNode: null
    });
  }

  openCompareTagsModal = (node) => {
    this.setState({
      compareTagsModal: true,
      editNode: { ...node }
    });
  }

  closeCompareTagsModal = () => {
    this.setState({
      compareTagsModal: false,
      editNode: null
    });
  }

  openDeleteOverallAlarmsModal = (node) => {
    this.setState({
      deleteOverallAlarmsModal: true,
      editNode: { ...node }
    });
  }

  closeDeleteOverallAlarmsModal = () => {
    this.setState({
      deleteOverallAlarmsModal: false,
      editNode: null
    });
  }

  setCopiedAssetDetails = details => this.setState({ copiedAssetDetails: details })

  getAlertMessage = () => {
    const { editNode: { node_type } } = this.state;
    const { t } = this.props;
    let placeholder = t('assetHierarchy.delete.placeholders.thisNodeType', { nodeType: node_type });
    if (node_type === 'config') placeholder = t('assetHierarchy.delete.placeholders.thisChart');
    else if (node_type === 'tags_envelopes') placeholder = t('assetHierarchy.delete.placeholders.allEnvelopeAlarmsOnThisAsset');
    return t('assetHierarchy.delete.confirm', { placeholder });
  }

  getAlertMessageforInactiveTagsDeletion = () => {
    const { editNode: { node_type } } = this.state;
    const { t } = this.props;
    const placeholder = t('assetHierarchy.delete.placeholders.thisNodeType', { nodeType: node_type });
    return t('assetHierarchy.delete.confirmInactiveTags', { placeholder });
  }

  getHierarchyFilterSvg = () => {
    const { nodeFilterSelected, nodeFilterExpanded } = this.state;
    return hierarchyUtils.getSvg(
      (nodeFilterSelected || 'filter'),
      (nodeFilterExpanded || nodeFilterSelected)
    );
  }


  sortHierarchy = (nodes, sortHierarchyAscending) => {
    const { currentAccount } = this.props;
    let treeNodes = hierarchyUtils.sortNodeList(nodes, sortHierarchyAscending);
    treeNodes = hierarchyUtils.sortLocationConfigs(treeNodes, currentAccount.preferences.vibration_direction_preference);
    return treeNodes;
  };


  setTreeNodesAndConfigList = (nodes = this.props.assetHierarchy.hierarchy) => {
    const treeNodes = this.sortHierarchy(_.cloneDeep(nodes), this.sortHierarchyAscending);
    this.setState({
      treeNodes,
      configList: hierarchyUtils.getNodeListFromHierarchy(treeNodes, 'config')
    }, this.setCurrentConfigIndex);
  }

  setSortOrder = (sortAscending) => {
    if (this.sortHierarchyAscending === sortAscending) return;
    this.sortHierarchyAscending = sortAscending;
    localStorage.setItem('sortHierarchyAscending', JSON.stringify(sortAscending));
    this.setTreeNodesAndConfigList(this.state.treeNodes);
  }

  restoreOrginalOrder = () => {
    if (this.sortHierarchyAscending !== null) {
      this.sortHierarchyAscending = null;
      localStorage.setItem('sortHierarchyAscending', null);
      this.setTreeNodesAndConfigList();
    }
  }

  render() {
    const {
      currentConfigIndex,
      configList,
      deleteModal,
      deleteInactiveTagsModal,
      addAreaModal,
      editAreaModal,
      node,
      editNode,
      assetCopyModal,
      assetPasteModal,
      deleteOverallAlarmsModal,
      nodeFilterExpanded,
      nodeFilterSelected,
      searchKey,
      searchActive,
      treeNodes,
      addTrendModal,
      compareTagsModal,
      cloneMachinesModal,
      cloneTargetAccountId,
      cloneTaskProgressList,
      cloneProgressExpanded,
      cloneOverlayHeight,
      cloneTaskPendingDismissal
    } = this.state;
    const { breadcrumb, hierarchyViewPane, primaryColor } = this.props;
    // Deliberately NOT scoped to the account in view: showing it everywhere means you
    // can navigate away and still watch a clone finish. The store is keyed by user id,
    // so it stays private to whoever started the clone.
    const visibleCloneTasks = cloneTaskProgressList || [];

    return (
    <>
      {!!cloneTaskPendingDismissal && (
        <AlertPrompt
          message={this.props.t('assetHierarchy.cloneMachines.dismissRunningTitle', {
            defaultValue: 'Stop tracking the clone to {{siteName}}?',
            siteName: cloneTaskPendingDismissal.targetAccountName
          })}
          secondaryMessage={this.props.t('assetHierarchy.cloneMachines.dismissRunningWarning', {
            defaultValue: 'The clone keeps running, but this is the only place its progress is shown. Dismiss it and you will not be able to see the progress again.'
          })}
          onCancel={this.cancelCloneTaskDismissal}
          onProceed={() => this.dismissCloneTask(cloneTaskPendingDismissal.taskId)}
        />
      )}
      {deleteModal && (
        <AlertPrompt
          message={this.getAlertMessage()}
          secondaryMessage={this.props.t('common.actionCannotBeUndone', { defaultValue: 'This action can not be undone' })}
          onCancel={this.closeDeleteModal}
          onProceed={() => this.deleteNode(editNode)}
        />
      )}
      {deleteInactiveTagsModal && (
        <AlertPrompt
          message={this.getAlertMessageforInactiveTagsDeletion()}
          secondaryMessage={this.props.t('common.actionCannotBeUndone', { defaultValue: 'This action can not be undone' })}
          infoMessage={this.props.t('assetHierarchy.deleteInactiveTags.infoMessage', {
            defaultValue: 'Data from disassociated sensors are considered as inactive tags'
          })}
          onCancel={this.toggleDeleteInactiveTagsModal}
          onProceed={() => this.deleteInactiveTags(editNode)}
        />
      )}
      {editAreaModal && (
        <AddAreaModal
          node={editNode}
          closeModal={this.closeAreaEditModal}
          editMode
          scrollNodeInView={this.scrollNodeInView}
        />
      )}
      {addAreaModal && (
        <AddAreaModal
          node={node}
          closeModal={this.closeAddNodeModal}
          scrollNodeInView={this.scrollNodeInView}
        />
      )}
      {assetCopyModal && (
        <AssetCopyModal
          assetInfo={editNode}
          setCopiedAssetDetails={this.setCopiedAssetDetails}
          closeModal={this.closeAssetCopyModal}
        />
      )}
      {assetPasteModal && (
        <AssetPasteModal
          areaInfo={editNode}
          closeModal={this.closeAssetPasteModal}
          details={this.state.copiedAssetDetails}
        />
      )}
      {deleteOverallAlarmsModal && (
        <DeleteOverallAlarmsModal
          assetInfo={editNode}
          site={breadcrumb.site}
          closeModal={this.closeDeleteOverallAlarmsModal}
        />
      )
      }
      {addTrendModal && (
        <AddTrendModal
          close={this.closeAddTrendModal}
          machineId={editNode.id}
        />
      )}
      {compareTagsModal && (
        <CompareTagsModal
          close={this.closeCompareTagsModal}
          machine={editNode.node_type === 'machine' ? editNode : undefined}
        />
      )}
      {cloneMachinesModal && cloneTargetAccountId && (
        <CloneMachinesModal
          targetAccountId={cloneTargetAccountId}
          closeModal={this.closeCloneMachinesModal}
          fetchSourceSites={this.fetchSourceSites}
          fetchSourceMachines={this.fetchSourceMachines}
          submitCloneToAccount={this.submitCloneToAccount}
          cloneTaskProgressList={cloneTaskProgressList}
          onCloneTaskStarted={this.handleCloneTaskStarted}
          onCloneCompleted={this.handleCloneCompleted}
        />
      )}
      <Content direction="column">
        <HeaderContainer isVisible={!!hierarchyViewPane.currentWidth}>
          <Filter
            id="hierarchy-filter"
            open={nodeFilterExpanded}
            toggleOpen={this.toggleFilter}
            Icon={this.getHierarchyFilterSvg}
            alignHorizontal="left"
          >
            <FlexContainer direction="column" id="hierarchy-filter-options">
              {this.filterOptions.map((option, idx) => {
                const selected = option.node_type === nodeFilterSelected;
                return (
                  <FilterItem
                    key={idx.toString()}
                    width="119px"
                    selected={selected}
                    onClick={() => this.handleFilterItemClick(option)}
                  >
                    {hierarchyUtils.getSvg(option.node_type, selected)}
                    <ListOption selected={selected}>{this.props.t(option.textKey)}</ListOption>
                  </FilterItem>
                );
              })}
            </FlexContainer>
          </Filter>
          <InputField
            name="hierarchy-search"
            onFocus={() => this.setState({ searchActive: true })}
            onBlur={() => !searchKey && this.setState({ searchActive: false })}
            type="text"
            prefix={<Search />}
            prefixSide="left"
            onChange={e => this.setSearchKey(e.target.value)}
            value={this.state.searchKey}
            placeholder={this.props.t('assetHierarchy.searchPlaceholder')}
            searchBox
            clearable
            clearInput={() => this.setSearchKey('')}
          />
          {(!searchActive && !nodeFilterSelected) && (
            <RBAC
              resource={mapComponentToResource.HierarchyView}
              operation={operations.Update}
              yes={(
                <SvgRowContainer>
                  <FlexContainer>
                    <SvgContainer
                      onClick={this.onClickUpArrow}
                    >
                      <ChevronUp style={{ color: currentConfigIndex < 1 ? '#D8D8D6' : '#999B95' }} />
                    </SvgContainer>
                    <SvgContainer
                      onClick={this.onClickDownArrow}
                    >
                      <ChevronDown style={{ color: currentConfigIndex >= configList.length - 1 ? '#D8D8D6' : '#999B95' }} />
                    </SvgContainer>
                    <SvgContainer onClick={() => this.onEdit(!this.props.editMode)}>
                      <EditIcon fill={this.props.editMode ? primaryColor : '#999B95'} />
                    </SvgContainer>
                  </FlexContainer>
                </SvgRowContainer>
              )}
              no={(
                <FlexContainer padding="0 2em 0 0">
                  <SvgRowContainer>
                    <SvgContainer
                      onClick={this.onClickUpArrow}
                    >
                      <ChevronUp style={{ color: currentConfigIndex < 1 ? '#D8D8D6' : '#999B95' }} />
                    </SvgContainer>
                    <SvgContainer
                      onClick={this.onClickDownArrow}
                    >
                      <ChevronDown style={{ color: currentConfigIndex >= configList.length - 1 ? '#D8D8D6' : '#999B95' }} />
                    </SvgContainer>
                  </SvgRowContainer>
                </FlexContainer>
              )}
            />
          )}
        </HeaderContainer>
        {this.props.orgBreadcrumb && this.props.orgBreadcrumb()}
        <CustomScrollContainer id="hierarchy" edit={this.props.editMode} open={!!hierarchyViewPane.currentWidth}>
        <ColumnLayout editMode={this.props.editMode}>
          {!searchKey && !nodeFilterSelected && (
            <LeftColumn
              onMouseEnter={this.addWindowEventListener}
              onMouseLeave={this.removeWindowEvenListener}
              width="100%"
            >
                <NodeList
                  treeNodes={treeNodes}
                  switchNode={this.selectNode}
                  selectedNodeId={breadcrumb.selected.id}
                  selectedNodeType={breadcrumb.selected.type}
                  reorderTree={this.reorderTree}
                  editMode={this.props.editMode}
                  getNodeMenuOptions={this.getNodeMenuOptions}
                />
                <CloneOverlaySpacer height={visibleCloneTasks.length ? cloneOverlayHeight : 0} />
            </LeftColumn>
          )}
          {(searchKey || nodeFilterSelected) && (
            <LeftColumn width="100%">
              <SearchList
                searchKey={searchKey}
                selectedNodeType={nodeFilterSelected}
                treeNodes={treeNodes}
                switchNode={this.selectNode}
                clearSearch={() => this.setSearchKey('')}
                clearFilter={() => this.selectFilter(null)}
                scrollNodeInView={this.scrollNodeInView}
              />
              <CloneOverlaySpacer height={visibleCloneTasks.length ? cloneOverlayHeight : 0} />
            </LeftColumn>
          )}
          {this.props.editMode && (
          <FooterFlex width={`${this.props.hierarchyViewPane.currentWidth}px`} alignHorizontal="right">
            <Button
              cancel
              onClick={this.onCancel}
            >
              Cancel
            </Button>
            <Button
              disabled={this.state.saveDisabled}
              onClick={this.onSave}
            >
              Save
            </Button>
          </FooterFlex>
          )}
        </ColumnLayout>
        </CustomScrollContainer>
        {/*
          The panel used to render on every hierarchy page for every user, even with
          no clone ever run. It now appears only when there is something to show, and
          only for users who can create machines.
        */}
        {!!visibleCloneTasks.length && (
        <RBAC
          resource={mapComponentToResource.MachineClone}
          operation={operations.Create}
          yes={(
        <CloneProgressSection direction="column" innerRef={this.cloneOverlayRef}>
          <CloneProgressHeader onClick={this.toggleCloneProgressExpanded}>
            <FlexContainer alignItems="center">
              <CloneProgressTitle>
                {this.props.t('assetHierarchy.cloneMachines.progressPanelTitle', { defaultValue: 'Clone progress' })}
              </CloneProgressTitle>
              <CloneProgressCount>{visibleCloneTasks.length}</CloneProgressCount>
            </FlexContainer>
            {cloneProgressExpanded
              ? <ChevronDown style={{ color: '#999B95' }} />
              : <ChevronUp style={{ color: '#999B95' }} />}
          </CloneProgressHeader>
          {cloneProgressExpanded && (
          <CloneProgressBody direction="column">
          {visibleCloneTasks.map((task) => {
            const completedMachines = Number(task.summary && task.summary.completed_machines) || 0;
            const totalMachines = Number(task.summary && task.summary.total_machines) || 0;
            const progressPercent = totalMachines > 0 ? Math.max(0, Math.min(100, Math.round((completedMachines / totalMachines) * 100))) : 0;
            const taskStatusTheme = cloneStatusTheme(task.status, this.props.theme);
            return (
                <CloneTaskCard key={task.taskId} direction="column">
                  <CloneTaskHeader>
                    <CloneTaskLabel title={task.targetAccountName}>
                      {(task.sourceAccountNames && task.sourceAccountNames.length)
                        ? this.props.t('assetHierarchy.cloneMachines.taskSourceToTarget', {
                          defaultValue: '{{sourceNames}} to {{siteName}}',
                          sourceNames: task.sourceAccountNames.join(', '),
                          siteName: task.targetAccountName
                        })
                        : this.props.t('assetHierarchy.cloneMachines.taskTarget', {
                          defaultValue: 'Target: {{siteName}}',
                          siteName: task.targetAccountName
                        })}
                    </CloneTaskLabel>
                    <FlexContainer alignItems="center">
                      <StatusBadge backgroundColor={taskStatusTheme.backgroundColor} color={taskStatusTheme.color}>
                        {normalizeMachineCloneStatus(task.status).replace('_', ' ')}
                      </StatusBadge>
                      <CloneTaskDismiss
                        title={this.props.t('assetHierarchy.cloneMachines.dismiss', { defaultValue: 'Dismiss' })}
                        onClick={() => this.requestCloneTaskDismissal(task)}
                      >
                        &times;
                      </CloneTaskDismiss>
                    </FlexContainer>
                  </CloneTaskHeader>
                  <CloneTaskMeta>
                    {this.props.t('assetHierarchy.cloneMachines.overallProgress', {
                      defaultValue: '{{completed}} / {{total}} machines cloned',
                      completed: completedMachines,
                      total: totalMachines
                    })}
                  </CloneTaskMeta>
                  <ProgressTrack>
                    <ProgressFill progress={progressPercent} />
                  </ProgressTrack>
                  {!!task.errorMessage && (
                    <CloneTaskMeta>
                      {task.errorMessage}
                    </CloneTaskMeta>
                  )}
                  {!!task.machineRows.length && (
                    <MachineRows direction="column">
                      {task.machineRows.map((machine) => {
                        const machineTheme = cloneStatusTheme(machine.status, this.props.theme);
                        // Only worth naming the source per row when the task pulled from
                        // more than one site, otherwise it repeats the header on every row.
                        const showRowSource = !!machine.sourceAccountName
                          && !!task.sourceAccountNames
                          && task.sourceAccountNames.length > 1;
                        return (
                          <MachineRow key={`${task.taskId}_${machine.id}`}>
                            <MachineName>
                              {showRowSource
                                ? `${machine.name} (${machine.sourceAccountName})`
                                : machine.name}
                            </MachineName>
                            <StatusBadge backgroundColor={machineTheme.backgroundColor} color={machineTheme.color}>
                              {normalizeMachineCloneStatus(machine.status).replace('_', ' ')}
                              {machine.new_machine_name ? ` (${machine.new_machine_name})` : ''}
                            </StatusBadge>
                          </MachineRow>
                        );
                      })}
                    </MachineRows>
                  )}
                </CloneTaskCard>
            );
          })}
          </CloneProgressBody>
          )}
        </CloneProgressSection>
          )}
        />
        )}
      </Content>
    </>
    );
  }
}

const mapStateToProps = state => ({
  assetHierarchy: state.assetHierarchyReducer.assetInfo,
  currentUser: state.user.user,
  currentAccount: state.currentAccount,
  breadcrumb: state.breadcrumb,
  hierarchyViewPane: state.hierarchyViewPane,
  primaryColor: state.companyReducer.partner.theme.primaryColor
});

const mapDispatchToProps = dispatch => ({
  assetDetailsActions: bindActionCreators(assetDetailsActions, dispatch),
  hierarchyUpdateActions: bindActionCreators(hierarchyUpdateActions, dispatch),
  machineInfoActions: bindActionCreators(machineInfoActions, dispatch),
  envelopeActions: bindActionCreators(envelopeActions, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(withTranslation()(HierarchyView)));
